'use client'

import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { UserListItem } from '@/components/ui/user-list-item'
import { cn } from '@/lib/utils'
import { ChevronDownIcon, SearchIcon, XIcon } from 'lucide-react'
import * as React from 'react'

export interface UserMultiSelectItem {
  optionLabel: string
  optionSubLabel: string
  value: string
}

export interface UserMultiSelectProps {
  items: UserMultiSelectItem[]
  value: string[]
  setValue: (value: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  renderItem?: (item: UserMultiSelectItem) => React.ReactNode
  emptyMessage?: string
  searchPlaceholder?: string
}

function DefaultUserMultiSelectItem({ item }: { item: UserMultiSelectItem }) {
  return <UserListItem label={item.optionLabel} subLabel={item.optionSubLabel} />
}

export function UserMultiSelect({
  items,
  value,
  setValue,
  placeholder = 'Select...',
  className,
  disabled,
  renderItem,
  emptyMessage = 'No options found.',
  searchPlaceholder = 'Search...',
}: UserMultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')

  const selectedValueSet = React.useMemo(() => new Set(value), [value])

  const selectedItems = React.useMemo(
    () => items.filter((item) => selectedValueSet.has(item.value)),
    [items, selectedValueSet],
  )

  const filteredItems = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()

    return items.filter((item) => {
      if (selectedValueSet.has(item.value)) return false
      if (!normalizedQuery) return true

      return [item.optionLabel, item.optionSubLabel, item.value].some((text) =>
        text.toLowerCase().includes(normalizedQuery),
      )
    })
  }, [items, query, selectedValueSet])

  const addItem = (itemValue: string) => {
    if (selectedValueSet.has(itemValue)) return
    setValue([...value, itemValue])
  }

  const removeItem = (itemValue: string) => {
    setValue(value.filter((selectedValue) => selectedValue !== itemValue))
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (!disabled) setOpen(nextOpen)
      }}
    >
      <PopoverTrigger
        disabled={disabled}
        nativeButton={false}
        render={<div />}
        className={cn(
          'flex w-full cursor-pointer items-center gap-1 rounded-lg border border-[#E5E7EB] bg-white p-2 text-xs text-[#1E1E1E] transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 data-disabled:cursor-not-allowed data-disabled:opacity-50',
          className,
        )}
      >
        <span className="flex max-h-[76px] min-h-6 min-w-0 flex-1 flex-wrap items-center gap-1.5 overflow-y-auto px-1">
          {selectedItems.length === 0 ? (
            <span
              className={cn(
                'line-clamp-1 text-comp-input-ip1 leading-comp-input-ip1',
                disabled ? 'text-creation-placeholder-text-disable' : 'text-creation-placeholder-text-default',
              )}
            >
              {placeholder}
            </span>
          ) : (
            selectedItems.map((item) => (
              <Badge
                key={item.value}
                color="outline"
                className="h-6 max-w-full gap-1 rounded-full border-[#D1D5DB] bg-[#2D2D2D]/5 px-2 text-[12px] leading-4 text-[#555F6D]"
              >
                <span className="truncate">{item.optionLabel}</span>
                <button
                  type="button"
                  aria-label={`Remove ${item.optionLabel}`}
                  disabled={disabled}
                  onPointerDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    removeItem(item.value)
                  }}
                  className="inline-flex size-4 shrink-0 items-center justify-center rounded-full hover:bg-[#1E1E1E]/10 disabled:pointer-events-none"
                >
                  <XIcon className="size-3" />
                </button>
              </Badge>
            ))
          )}
        </span>
        <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--anchor-width) p-1">
        <div className="relative mb-1">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-[#9EA8B3]" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            className="h-9 w-full rounded-md border border-[#E5E7EB] bg-white pr-3 pl-8 text-sm text-[#1E1E1E] outline-none placeholder:text-[#B3B3B3] focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30"
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-[#7E8B99]">{emptyMessage}</div>
          ) : (
            filteredItems.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => addItem(item.value)}
                className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
              >
                {renderItem ? renderItem(item) : <DefaultUserMultiSelectItem item={item} />}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
